import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import MotionNumber from "motion-number";
import { easeOut } from "framer-motion";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card/hover-card";

import { Separator } from "@/components/ui/separator/separator";
import { Report1DateRangePicker } from "./date-range-picker";
import { useDateRangeStore } from "./use-date-range-store";
import { Info } from "lucide-react";
import { fetchLeadsDateWiseAdmin } from "@/config/queries/admin/admin-queries";
import { useParams } from "react-router-dom";

function adjustDates(
  d1: Date,
  d2: Date
): { adjustedD1: Date | string; adjustedD2: Date | string } {
  // Create new Date objects to avoid mutating the original inputs
  const adjustedD1 = new Date(d1);
  const adjustedD2 = new Date(d2);

  // Set d1 time to 00:00:00
  adjustedD1.setUTCHours(0, 0, 0, 0);
  adjustedD1.setUTCDate(adjustedD1.getUTCDate() + 1);

  // Set d2 time to 00:00:00 and add 1 day
  adjustedD2.setUTCHours(0, 0, 0, 0);
  adjustedD2.setUTCDate(adjustedD2.getUTCDate() + 1);

  // Return the adjusted dates
  return { adjustedD1, adjustedD2 };
}

function adjustDatesFirstTime(
  d1: Date,
  d2: Date
): { adjustedD1: Date | string; adjustedD2: Date | string } {
  // Create new Date objects to avoid mutating the original inputs
  const adjustedD1 = new Date(d1);
  const adjustedD2 = new Date(d2);

  // Set d1 time to 00:00:00
  adjustedD1.setUTCHours(0, 0, 0, 0);

  // Set d2 time to 00:00:00 and add 1 day
  adjustedD2.setUTCHours(0, 0, 0, 0);
  adjustedD2.setUTCDate(adjustedD2.getUTCDate() + 1);

  // Return the adjusted dates
  return { adjustedD1, adjustedD2 };
}

export const LeadReport1 = () => {
  const key = Cookies.get("key") as string;

  const { empId } = useParams() as {
    empId: string;
  };

  const [firstTime, setFirstTime] = useState(true);

  const { dateRange } = useDateRangeStore();

  const startdate = dateRange?.from as Date;
  const enddate = dateRange?.to as Date;

  const adjusedDates = useMemo(() => {
    const { adjustedD1, adjustedD2 } = adjustDates(startdate, enddate);

    return {
      startdate: adjustedD1,
      enddate: adjustedD2,
    };
  }, [startdate, enddate]);

  const adjusedDatesFirstTime = useMemo(() => {
    const { adjustedD1, adjustedD2 } = adjustDatesFirstTime(startdate, enddate);

    return {
      startdate: adjustedD1,
      enddate: adjustedD2,
    };
  }, [startdate, enddate]);

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["datewise-leads", startdate, enddate, empId],
    queryFn: () =>
      fetchLeadsDateWiseAdmin(key)({
        employee: empId,
        startdate: firstTime
          ? (adjusedDatesFirstTime.startdate as Date)
          : (adjusedDates.startdate as Date),
        enddate: firstTime
          ? (adjusedDatesFirstTime.enddate as Date)
          : (adjusedDates.enddate as Date),
      }),
    enabled: !!empId,
  });

  const datewiseData = useMemo(() => {
    const defaultData = {
      attempted: 0,
      success: 0,
      failed: 0,
      alloted: 0,
      open: 0,
    };

    if (isPending) {
      return defaultData;
    }

    if (data === undefined || data?.reports === undefined) {
      return defaultData;
    }

    defaultData.attempted = data?.reports?.attempted;
    defaultData.open = data?.reports?.open;
    defaultData.alloted = data?.reports?.alloted;
    defaultData.success = data?.reports?.closed?.success;
    defaultData.failed = data?.reports?.closed?.failed;

    return defaultData;
  }, [data, isPending, isFetching, empId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Report 1</CardTitle>
        <CardDescription>
          This report represent the number of attempted leads, number of success
          leads and number of failure leads for a day or a given range of dates.
        </CardDescription>
      </CardHeader>

      <Separator />

      <CardContent className="pt-6">
        <div className="flex gap-5">
          <div>
            <Report1DateRangePicker
              firstTime={firstTime}
              firstTimeChange={() => {
                setFirstTime(false);
              }}
            />
          </div>
        </div>

        <div className="flex flex-row flex-wrap gap-4 mt-6">
          <div className="border shadow rounded p-3 px-5 flex flex-col gap-2 lg:w-[200px] w-full">
            <div className="flex flex-row items-center justify-between">
              <div className="text-lg">Assigned</div>
              <div>
                <HoverCard>
                  <HoverCardTrigger>
                    <Info className="w-4 h-4 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent>
                    "Assigned" represent number of leads you assigned to
                    yourself.
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>

            <div className="text-xl font-semibold">
              <MotionNumber
                value={datewiseData.alloted}
                transition={{
                  // Applied to layout animations on individual characters:
                  layout: { type: "spring", duration: 0.7, bounce: 0 },
                  // Used for the digit animations:
                  y: { type: "spring", duration: 0.7, bounce: 0.25 },

                  // Opacity applies to entering/exiting characters.
                  // Note the use of the times array, explained below:
                  opacity: { duration: 0.7, ease: easeOut, times: [0, 0.3] }, // 0.3s perceptual duration
                }}
              />
            </div>
          </div>

          <div className="border shadow rounded p-3 px-5 flex flex-col gap-2 lg:w-[200px] w-full">
            <div className="flex flex-row items-center justify-between">
              <div className="text-lg">Attempted</div>
              <div>
                <HoverCard>
                  <HoverCardTrigger>
                    <Info className="w-4 h-4 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent>
                    "Attempted" represent number of leads you interacted with.
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>

            <div className="text-xl font-semibold">
              <MotionNumber
                value={datewiseData.attempted}
                transition={{
                  // Applied to layout animations on individual characters:
                  layout: { type: "spring", duration: 0.7, bounce: 0 },
                  // Used for the digit animations:
                  y: { type: "spring", duration: 0.7, bounce: 0.25 },

                  // Opacity applies to entering/exiting characters.
                  // Note the use of the times array, explained below:
                  opacity: { duration: 0.7, ease: easeOut, times: [0, 0.3] }, // 0.3s perceptual duration
                }}
              />
            </div>
          </div>

          <div className="border shadow rounded p-3 px-5 flex flex-col gap-2 lg:w-[200px] w-full">
            <div className="flex flex-row items-center justify-between">
              <div className="text-lg">Success</div>
              <div>
                <HoverCard>
                  <HoverCardTrigger>
                    <Info className="w-4 h-4 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent>
                    "Success" represent number of leads you closed successfully.
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>

            <div className="text-xl font-semibold">
              <MotionNumber
                value={datewiseData.success}
                transition={{
                  // Applied to layout animations on individual characters:
                  layout: { type: "spring", duration: 0.7, bounce: 0 },
                  // Used for the digit animations:
                  y: { type: "spring", duration: 0.7, bounce: 0.25 },

                  // Opacity applies to entering/exiting characters.
                  // Note the use of the times array, explained below:
                  opacity: { duration: 0.7, ease: easeOut, times: [0, 0.3] }, // 0.3s perceptual duration
                }}
              />
            </div>
          </div>

          {/* <div className="border shadow rounded p-3 px-5 flex flex-col gap-2 lg:w-[200px] w-full">
            <div className="flex flex-row items-center justify-between">
              <div className="text-lg">Failed</div>
              <div>
                <HoverCard>
                  <HoverCardTrigger>
                    <Info className="w-4 h-4 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent>
                    "Failed" represent number of leads you marked as "Not
                    Interested".
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>

            <div className="text-xl font-semibold">
              <MotionNumber
                value={datewiseData.failed}
                transition={{
                  // Applied to layout animations on individual characters:
                  layout: { type: "spring", duration: 0.7, bounce: 0 },
                  // Used for the digit animations:
                  y: { type: "spring", duration: 0.7, bounce: 0.25 },

                  // Opacity applies to entering/exiting characters.
                  // Note the use of the times array, explained below:
                  opacity: { duration: 0.7, ease: easeOut, times: [0, 0.3] }, // 0.3s perceptual duration
                }}
              />
            </div>
          </div> */}

          <div className="border shadow rounded p-3 px-5 flex flex-col gap-2 lg:w-[200px] w-full">
            <div className="flex flex-row items-center justify-between">
              <div className="text-lg">Open</div>
              <div>
                <HoverCard>
                  <HoverCardTrigger>
                    <Info className="w-4 h-4 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent>
                    "Open" represent number of leads you worked on and that are
                    neither closed successfully nor marked as "Not Interested".
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>

            <div className="text-xl font-semibold">
              <MotionNumber
                value={datewiseData.failed}
                transition={{
                  // Applied to layout animations on individual characters:
                  layout: { type: "spring", duration: 0.7, bounce: 0 },
                  // Used for the digit animations:
                  y: { type: "spring", duration: 0.7, bounce: 0.25 },

                  // Opacity applies to entering/exiting characters.
                  // Note the use of the times array, explained below:
                  opacity: { duration: 0.7, ease: easeOut, times: [0, 0.3] }, // 0.3s perceptual duration
                }}
              />
            </div>
          </div>
        </div>

        {/* <div>
          <Report1Chart data={datewiseData} />
        </div> */}
      </CardContent>
    </Card>
  );
};
